import React, { useState, useEffect, useCallback } from 'react';
import styles from './MainPage.module.css';
import Calendar from 'components/common/Calendar/Calendar';

import MachineListTab from 'pages/machine/MachineListTab';
import MachineFirmwareTab from 'pages/machine/MachineFirmwareTab';
import MachineGroupTab from 'pages/machine/MachineGroupTab';
import MachineInterfaceTab from 'pages/machine/MachineInterfaceTab';
import MachineTemplateTab from 'pages/machine/MachineTemplateTab';

import ProductListTab from 'pages/product/ProductListTab';
import ProductTypeTab from 'pages/product/ProductTypeTab';
import ProductUnitTab from 'pages/product/ProductUnitTab';
import ProductBrandTab from 'pages/product/ProductBrandTab';

import SaleStatusTab from 'pages/sales/SaleStatusTab';
import SaleManagementTab from 'pages/sales/SaleManagementTab';

import ManagerListTab from 'pages/manager/ManagerListTab';
import PositionManagementTab from 'pages/manager/PositionManagementTab';

import Api from 'utils/api';

import { useNavigate, useLocation } from 'react-router-dom';


const MainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();


  const [activeNav, setActiveNav] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isMachineOpen, setIsMachineOpen] = useState(false);
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);

  const [todaySales, setTodaySales] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [salesData, setSalesData] = useState({}); // 캘린더용 데이터
  const [loading, setLoading] = useState(false);

  // --- 추가: 캘린더가 보여주는 연/월 상태 ---
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);


  const [machineStatus, setMachineStatus] = useState({ total: 0, active: 0 });
  const [soldOutCount, setSoldOutCount] = useState(0);


  const handleLogout = async () => {
    const confirmLogout = window.confirm("로그아웃 하시겠습니까?");
    if (!confirmLogout) return;

    try {
      await fetch('http://localhost:8080/api/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'AccessToken': `Bearer ${localStorage.getItem('AccessToken')}`,
        },
      });

      localStorage.removeItem('AccessToken');
      alert("로그아웃 되었습니다.");
      window.location.href = '/login';
    } catch (error) {
      console.error("로그아웃 실패:", error);
      alert("로그아웃 중 오류가 발생했습니다.");
    }
  };

  // --- API 호출 함수 (품절 현황) ---
  const fetchSoldOutStatus = useCallback(async () => {
    const data = await Api.get(`/machine/column/sold-out-status`);
    return data.machineColumnSoldOutStatus?.columnSoldOutCount || 0;

  }, []);

  // --- API 호출 함수 (운영기기 현황) ---
  const fetchMachineStatus = useCallback(async () => {
    const data = await Api.get('/machine/operating-status')
    const status = data.machineOperatingStatus;
    return {
      total: status.totalMachineCount,
      active: status.operatingMachineCount
    };
  }, []);

  // --- API 호출 공통 함수 (매출 합계용) ---
  const fetchRevenueSum = useCallback(async (start, end) => {
    const data = await Api.get(`/sale/revenue/sum?startLocalDateTime=${start}&endLocalDateTime=${end}`)

    return data.saleRevenue || 0;

  }, []);

  // --- API 호출 함수 (캘린더 일별 매출용) ---
  const fetchDailyRevenue = useCallback(async (start, end) => {

    const data = await Api.get(`/sale/revenue/date?startLocalDateTime=${start}&endLocalDateTime=${end}`);
    return data.saleRevenueMap || {};

  }, []);

  // --- 캘린더 월 이동 시 호출될 함수 ---
  // Calendar 컴포넌트 내부에서 월이 바뀔 때 이 함수를 호출해야 함
  const handleFetchMonthData = useCallback(async (startISO, endISO) => {
    setLoading(true);

    // ISO 문자열(YYYY-MM-DD...)에서 연, 월 추출
    const [year, month] = startISO.split('-');
    setViewYear(parseInt(year));
    setViewMonth(parseInt(month));

    const dailyRes = await fetchDailyRevenue(startISO, endISO);
    const totalRes = await fetchRevenueSum(startISO, endISO);

    setSalesData(dailyRes);
    setMonthlyTotal(totalRes);
    setLoading(false);
  }, [fetchDailyRevenue, fetchRevenueSum]);



  // 날짜 포맷팅 헬퍼 함수 (YYYY-MM-DDTHH:mm:ss 형식 생성)
  const formatLocalISO = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();


    // 1. 오늘 기준 시간 설정
    // todayStart: 오늘 00:00:00
    // todayEnd: 내일 00:00:00
    const todayStart = formatLocalISO(new Date(y, m, d, 0, 0, 0));
    const todayEnd = formatLocalISO(new Date(y, m, d + 1, 0, 0, 0));

    // 2. 이번 달 기준 시간 설정
    // monthStart: 이번 달 1일 00:00:00
    // monthEnd: 다음 달 1일 00:00:00
    const monthStart = formatLocalISO(new Date(y, m, 1, 0, 0, 0));
    const monthEnd = formatLocalISO(new Date(y, m + 1, 1, 0, 0, 0));

    const machineRes = await fetchMachineStatus();
    const todayRes = await fetchRevenueSum(todayStart, todayEnd);
    const monthRes = await fetchRevenueSum(monthStart, monthEnd);
    const dailyRes = await fetchDailyRevenue(monthStart, monthEnd);
    const soldOutRes = await fetchSoldOutStatus();

    setMachineStatus(machineRes);
    setTodaySales(todayRes);
    setMonthlyTotal(monthRes);
    setSalesData(dailyRes);
    setSoldOutCount(soldOutRes); // ✅ 상태 업데이트
    setLoading(false);
  }, [fetchMachineStatus, fetchRevenueSum, fetchDailyRevenue, fetchSoldOutStatus]);

  useEffect(() => {
    if (activeNav === '홈') {
      loadDashboardData();
    }
  }, [activeNav, loadDashboardData]);


  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const closeAllAccordions = () => {
    setIsMachineOpen(false);
    setIsManagerOpen(false);
    setIsProductOpen(false);
    setIsSalesOpen(false);
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin') setActiveNav('홈');
    else if (path === '/admin/machine/list') {
      setActiveNav('장비 목록');
      setIsMachineOpen(true);
    } else if (path === '/admin/machine/template') {
      setActiveNav('장비 템플릿 관리');
      setIsMachineOpen(true);
    } else if (path === '/admin/machine/group') {
      setActiveNav('장비 그룹 관리');
      setIsMachineOpen(true);
    } else if (path === '/admin/machine/firmware') {
      setActiveNav('장비 펌웨어 관리');
      setIsMachineOpen(true);
    } else if (path === '/admin/machine/interface') {
      setActiveNav('장비 인터페이스 관리');
      setIsMachineOpen(true);
    }

    else if (path === '/admin/manager/list') {
      setActiveNav('직원 목록');
      setIsManagerOpen(true);
    } else if (path === '/admin/manager/position') {
      setActiveNav('직책 관리');
      setIsManagerOpen(true);
    }


    else if (path === '/admin/product/list') {
      setActiveNav('상품 목록');
      setIsProductOpen(true);
    } else if (path === '/admin/product/type') {
      setActiveNav('상품 분류 관리');
      setIsProductOpen(true);
    } else if (path === '/admin/product/unit') {
      setActiveNav('상품 단위 관리');
      setIsProductOpen(true);
    } else if (path === '/admin/product/brand') {
      setActiveNav('상품 브랜드 관리');
      setIsProductOpen(true);
    }

    else if (path === '/admin/sale/status') {
      setActiveNav('매출 현황');
      setIsSalesOpen(true);
    } else if (path === '/admin/sale/management') {
      setActiveNav('통합 매출 관리');
      setIsSalesOpen(true);
    }



  }, [location.pathname]);


  const handleMainMenuClick = (menu) => {

    if (menu === '홈') {
      setActiveNav('홈');
      navigate('/admin');
      setIsMenuOpen(false);
      closeAllAccordions(); // ✅ 추가: 열려있는 아코디언 메뉴들도 닫기 (선택 사항)
    } else if (menu === '장비관리') {
      setIsMachineOpen(!isMachineOpen);
      setIsManagerOpen(false); setIsProductOpen(false); setIsSalesOpen(false);
    } else if (menu === '직원관리') {
      setIsManagerOpen(!isManagerOpen);
      setIsMachineOpen(false); setIsProductOpen(false); setIsSalesOpen(false);
    } else if (menu === '상품관리') {
      setIsProductOpen(!isProductOpen);
      setIsMachineOpen(false); setIsManagerOpen(false); setIsSalesOpen(false);
    } else if (menu === '매출관리') {
      setIsSalesOpen(!isSalesOpen);
      setIsMachineOpen(false); setIsManagerOpen(false); setIsProductOpen(false);
    } else {
      setActiveNav(menu);
      setIsMenuOpen(false);
      closeAllAccordions();
    }
  };

  const handleSubMenuClick = (subMenu) => {
    if (subMenu === '장비 목록') navigate('/admin/machine/list');
    else if (subMenu === '장비 템플릿 관리') navigate('/admin/machine/template');
    else if (subMenu === '장비 그룹 관리') navigate('/admin/machine/group');
    else if (subMenu === '장비 펌웨어 관리') navigate('/admin/machine/firmware');
    else if (subMenu === '장비 인터페이스 관리') navigate('/admin/machine/interface');

    else if (subMenu === '상품 목록') navigate('/admin/product/list');
    else if (subMenu === '상품 분류 관리') navigate('/admin/product/type');
    else if (subMenu === '상품 단위 관리') navigate('/admin/product/unit');
    else if (subMenu === '상품 브랜드 관리') navigate('/admin/product/brand');

    else if (subMenu === '직원 목록') navigate('/admin/manager/list');
    else if (subMenu === '직책 관리') navigate('/admin/manager/position');

    else if (subMenu === '매출 현황') navigate('/admin/sale/status');
    else if (subMenu === '통합 매출 관리') navigate('/admin/sale/management');


    else {
      setActiveNav(subMenu);
    }

    setIsMenuOpen(false);
  };

  // 시간 포맷팅용 헬퍼 (현재 시간 표시용)
  const getFormattedNow = () => {
    const now = new Date();
    return `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const renderContent = () => {

    // 1. 장비관리 페이지 연결
    // if (machineSubs.includes(activeNav)) {
    //   return <MachinePage activeSubTab={activeNav} />;
    // }

    if (activeNav === '장비 목록') {
      return <MachineListTab />;
    }
    if (activeNav === '장비 템플릿 관리') {
      return <MachineTemplateTab />;
    }
    if (activeNav === '장비 그룹 관리') {
      return <MachineGroupTab />;
    }
    if (activeNav === '장비 펌웨어 관리') {
      return <MachineFirmwareTab />;
    }
    if (activeNav === '장비 인터페이스 관리') {
      return <MachineInterfaceTab />;
    }


    if (activeNav === '상품 목록') {
      return <ProductListTab />;
    }
    if (activeNav === '상품 분류 관리') {
      return <ProductTypeTab />;
    }
    if (activeNav === '상품 단위 관리') {
      return <ProductUnitTab />;
    }
    if (activeNav === '상품 브랜드 관리') {
      return <ProductBrandTab />;
    }


    // 2. 직원관리 메뉴 연결
    if (activeNav === '직원 목록') {
      return <ManagerListTab />;
    }
    if (activeNav === '직책 관리') {
      return <PositionManagementTab />;
    }


    if (activeNav === '매출 현황') {
      return <SaleStatusTab />;
    }

    if (activeNav === '통합 매출 관리') {
      return <SaleManagementTab />;
    }


    // 4. 홈 대시보드
    switch (activeNav) {
      case '홈':
        return (
          <>
            <section className={styles.salesCard}>
              <h2 className={styles.label}>오늘의 매출 (실시간)</h2>
              <div className={styles.salesValue}>
                {loading ? "로딩 중..." : `${todaySales.toLocaleString()}원`}
              </div>
            </section>
            <div className={styles.gridRow}>
              <section className={styles.infoCard}>
                <h2 className={styles.label}>운영기기 현황</h2>
                <div className={styles.stat}>
                  <span className={styles.active}>{machineStatus.active || 0}</span>
                  <span className={styles.divider}>/</span>
                  <span>{machineStatus.total}</span>
                </div>
                <p className={styles.subLabel}>활성 / 총 기기</p>
              </section>
              <section
                className={`${styles.infoCard} ${styles.clickable}`}
                onClick={() => handleSubMenuClick('장비 목록')} // 클릭 시 관련 페이지 이동 기능 추가 가능
              >
                <h2 className={`${styles.label} ${styles.warning}`}>품절관리 ⚠️</h2>
                <div className={styles.stat}>
                  {loading ? "..." : soldOutCount} <span className={styles.unit}>컬럼</span>
                </div>
              </section>
            </div>
            <section className={styles.monthlyCard}>
              <h2 className={styles.monthlyLabel}>
                {viewYear}년 {String(viewMonth).padStart(2, '0')}월 매출
              </h2>
              <div className={styles.monthlyValue}>
                {loading ? "로딩 중..." : `${monthlyTotal.toLocaleString()}원`}

              </div>
            </section>
            <Calendar salesData={salesData}
              onFetchData={handleFetchMonthData}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {isMenuOpen && <div className={styles.overlay} onClick={toggleMenu} />}

      <nav className={`${styles.sideMenu} ${isMenuOpen ? styles.menuOpen : ''}`}>
        <div className={styles.menuHeader}>
          <div className={styles.menuLogo}>MUBIZ ADMIN</div>
          <button className={styles.closeBtn} onClick={toggleMenu}>✕</button>
        </div>

        <ul className={styles.menuList}>
          <li className={activeNav === '홈' ? styles.menuItemActive : styles.menuItem} onClick={() => handleMainMenuClick('홈')}>
            홈
          </li>

          <AccordionMenu
            title="장비관리"
            isOpen={isMachineOpen}
            activeNav={activeNav}
            items={['장비 목록', '장비 템플릿 관리', '장비 그룹 관리', '장비 펌웨어 관리', '장비 인터페이스 관리']}
            onMainClick={() => handleMainMenuClick('장비관리')}
            onSubClick={handleSubMenuClick}
            styles={styles}
          />

          <AccordionMenu
            title="직원관리"
            isOpen={isManagerOpen}
            activeNav={activeNav}
            items={['직원 목록', '직책 관리']}
            onMainClick={() => handleMainMenuClick('직원관리')}
            onSubClick={handleSubMenuClick}
            styles={styles}
          />

          <AccordionMenu
            title="상품관리"
            isOpen={isProductOpen}
            activeNav={activeNav}
            items={['상품 목록', '상품 분류 관리', '상품 단위 관리', '상품 브랜드 관리']}
            onMainClick={() => handleMainMenuClick('상품관리')}
            onSubClick={handleSubMenuClick}
            styles={styles}
          />

          <AccordionMenu
            title="매출관리"
            isOpen={isSalesOpen}
            activeNav={activeNav}
            items={['매출 현황', '통합 매출 관리']}
            onMainClick={() => handleMainMenuClick('매출관리')}
            onSubClick={handleSubMenuClick}
            styles={styles}
          />
        </ul>

        <div className={styles.menuFooter}>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </nav>

      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.menuBtn} onClick={toggleMenu}>☰</button>
          <h1 className={styles.title}>
            {activeNav === '홈' ? '대시보드' : activeNav}
          </h1>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.companyName}>{localStorage.getItem('companyName')}</div>
          <div className={styles.updateTime}>{getFormattedNow()} 기준</div>
        </div>

      </header>

      <main className={styles.content}>
        {renderContent()}
      </main>
    </div>
  );
};

const AccordionMenu = ({ title, isOpen, activeNav, items, onMainClick, onSubClick, styles }) => {
  const isAnySubActive = items.includes(activeNav);
  return (
    <li>
      <div
        className={isAnySubActive ? styles.menuItemActive : styles.menuItem}
        onClick={onMainClick}
      >
        <span>{title}</span>
        <span className={`${styles.chevron} ${isOpen ? styles.rotate : ''}`}>▼</span>
      </div>
      {isOpen && (
        <ul className={styles.subMenuUl}>
          {items.map((sub) => (
            <li
              key={sub}
              className={activeNav === sub ? styles.subItemActive : styles.subItem}
              onClick={() => onSubClick(sub)}
            >
              • {sub}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default MainPage;